import React, { useState, useEffect, useRef } from 'react';
import { Check, ChevronRight, ArrowRight } from 'lucide-react';
import { useBracketStore } from '@/stores/bracketStore';
import { getTeamById as getStaticTeamById } from '@/data/teams';
import { useTeamsStore } from '@/stores/teamsStore';
import { useProjectedGame } from '@/hooks/useProjectedGame';
import { resolveMMERTeam, predictGame, loadMMERData, getFloorTeam, type MMERPrediction } from '@/lib/mmer-predict';
import TeamSlot from './TeamSlot';
import ConfidenceSlider from './ConfidenceSlider';
import toast from 'react-hot-toast';
import type { TournamentGame } from '@/data/tournament';
import { firstFourGames } from '@/data/tournament';

interface MatchupProps {
  game: TournamentGame;
  size?: 'sm' | 'md' | 'lg';
  showConfidence?: boolean;
  writeEnabled?: boolean;
  compact?: boolean;
  className?: string;
  isNextGameHint?: boolean;
  onPickComplete?: (gameId: string) => void;
  isPathSource?: boolean;
  onNextMatchup?: () => void;
}

const MatchupComponent: React.FC<MatchupProps> = ({
  game: rawGame,
  size = 'md',
  showConfidence = true,
  writeEnabled = true,
  compact = false,
  className = '',
  isNextGameHint = false,
  onPickComplete,
  isPathSource = false,
  onNextMatchup,
}) => {
  // Project team IDs from picks (so R2+ slots show user's picked winners)
  const game = useProjectedGame(rawGame);

  const [_showConfidenceSlider, _setShowConfidenceSlider] = useState(false);
  const bracket = useBracketStore(s => s.bracket);
  const updatePick = useBracketStore(s => s.updatePick);
  const loadBracket = useBracketStore((s) => s.loadBracket);
  const currentPick = useBracketStore(s => s.getPickForGame(game.id));
  const storeGetTeam = useTeamsStore(s => s.getTeam);
  const team1 = game.team1Id ? (storeGetTeam(game.team1Id) ?? getStaticTeamById(game.team1Id) ?? null) : null;
  const team2 = game.team2Id ? (storeGetTeam(game.team2Id) ?? getStaticTeamById(game.team2Id) ?? null) : null;


  const team1Available = game.team1Id && game.team1Id !== 'tbd';
  const team2Available = game.team2Id && game.team2Id !== 'tbd';
  const bothTeamsAvailable = !!(game.team1Id && game.team1Id !== 'tbd' && game.team2Id && game.team2Id !== 'tbd');
  const [showArrivalPulse, setShowArrivalPulse] = useState(false);
  const [showMatchupStats, setShowMatchupStats] = useState(false);
  const wasPendingRef = useRef(false);
  const anyTeamAvailable = !!(team1Available || team2Available);
  const feedingPlayIn = firstFourGames.find(g => g.nextGameId === rawGame.id);
  const isPlayInGame = !!(rawGame.isPlayIn || (team1?.playIn && team2?.playIn));

  const handleMatchupTapTarget = () => {
    setShowMatchupStats((prev) => !prev);
  };

  const vsHandledTouchRef = useRef(false);
  const centerTapTimerRef = useRef<number | undefined>(undefined);

  const handleCenterStripMouseClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (vsHandledTouchRef.current) {
      vsHandledTouchRef.current = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    handleMatchupTapTarget();
  };

  const handleCenterStripTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (centerTapTimerRef.current) {
      window.clearTimeout(centerTapTimerRef.current);
    }

    centerTapTimerRef.current = window.setTimeout(() => {
      vsHandledTouchRef.current = true;
      handleMatchupTapTarget();
    }, 220);
  };

  const handleCenterStripTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (centerTapTimerRef.current) {
      window.clearTimeout(centerTapTimerRef.current);
      centerTapTimerRef.current = undefined;
    }

    if (vsHandledTouchRef.current) {
      vsHandledTouchRef.current = false;
      return;
    }

    handleMatchupTapTarget();
  };

  const handleCenterStripPointerCancel = (event?: React.SyntheticEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (centerTapTimerRef.current) {
      window.clearTimeout(centerTapTimerRef.current);
      centerTapTimerRef.current = undefined;
    }
  };

  const selectedTeamId = currentPick?.teamId;
  const confidence = currentPick?.confidence || 5;

  const loadRouteBracketIfMissing = async () => {
    if (bracket) return true;
    if (typeof window === 'undefined') return false;
    const m = window.location.pathname.match(/\/bracket\/(.+?)$/);
    const routeId = m?.[1];
    if (!routeId) return false;
    await loadBracket(routeId);
    return !!useBracketStore.getState().bracket;
  };

  const isLocked = Boolean(bracket?.isLocked);
  const isHydrating = useBracketStore((s) => s.isBootstrapping || s.isLoadingBracket || s.isCreatingBracket);
  const isUpdatingPick = useBracketStore((s) => s.isUpdatingPick);
  const interactionDisabled = isLocked || isHydrating || isUpdatingPick || !writeEnabled;

  const handleTeamSelect = async (teamId: string) => {
    if (interactionDisabled || !teamId) return;

    try {
      const ready = await loadRouteBracketIfMissing();
      if (!ready) {
        toast.error('Could not load bracket. Please try again.');
        return;
      }
      await updatePick(game.id, teamId, confidence);
      onPickComplete?.(game.id);
    } catch (error: any) {
      console.error('Failed to update pick:', error);
      toast.error(error?.message || 'Failed to update pick');
    }
  };

  const handleConfidenceChange = async (newConfidence: number) => {
    if (interactionDisabled || !selectedTeamId) return;

    try {
      const ready = await loadRouteBracketIfMissing();
      if (!ready) {
        toast.error('Could not load bracket. Please try again.');
        return;
      }
      await updatePick(game.id, selectedTeamId, newConfidence);
      onPickComplete?.(game.id);
    } catch (error: any) {
      console.error('Failed to update confidence:', error);
      toast.error(error?.message || 'Failed to update confidence');
    }
  };

  const getMatchupStatus = () => {
    if (!anyTeamAvailable) return 'pending';
    if (currentPick) {
      if (currentPick.isCorrect === true) return 'correct';
      if (currentPick.isCorrect === false) return 'incorrect';
      return 'selected';
    }
    return 'available';
  };

  const matchupStatus = getMatchupStatus();
  // MMER prediction — load data on mount
  const [mmerPrediction, setMmerPrediction] = React.useState<MMERPrediction | null>(null);
  React.useEffect(() => {
    if (!team1?.shortName || !team2?.shortName) { setMmerPrediction(null); return; }
    let cancelled = false;
    loadMMERData().then(() => {
      if (cancelled) return;
      const a = resolveMMERTeam(team1.shortName || '');
      const b = resolveMMERTeam(team2.shortName || '');
      // Use floor team profile for unresolved teams (small programs not in MMER dataset)
      const aData = a || { espnId: 'floor', ...getFloorTeam() };
      const bData = b || { espnId: 'floor', ...getFloorTeam() };
      setMmerPrediction(predictGame(aData, bData, true));
    });
    return () => { cancelled = true; };
  }, [team1?.shortName, team2?.shortName]);

    const getTeamBaseWinPct = (team: typeof team1 | null): number | null => {
    if (!team) return null;

    // Use pre-computed tourney chance if available
    const rawChance = team.resume?.tourneyChance;
    if (typeof rawChance === 'number' && Number.isFinite(rawChance) && rawChance >= 0) {
      return rawChance > 1 ? Math.min(rawChance, 100) : rawChance * 100;
    }

    // Composite power score: blend NET ranking, SOS, offensive/defensive efficiency, and win%
    // Lower NET = better, lower SOS = harder schedule
    const net = team.resume?.netRanking ?? 200;
    const sos = team.resume?.sos ?? 200;
    const oRat = team.resume?.offensiveRating ?? 100;
    const dRat = team.resume?.defensiveRating ?? 110;
    const wins = team.record?.wins ?? 0;
    const losses = team.record?.losses ?? 0;
    const total = wins + losses;
    const winPct = total > 0 ? wins / total : 0.5;

    // Efficiency margin (higher = better)
    const effMargin = oRat - dRat;

    // Convert NET rank to a 0-100 power score (rank 1 = ~98, rank 350 = ~2)
    const netScore = Math.max(0, 100 - (net / 3.5));
    // SOS score (rank 1 = ~97, rank 350 = ~2)
    const sosScore = Math.max(0, 100 - (sos / 3.6));
    // Efficiency score (margin of +25 = ~95, margin of -10 = ~30)
    const effScore = Math.min(100, Math.max(0, 50 + effMargin * 2));
    // Win% score
    const wpScore = winPct * 100;

    // Weighted composite: NET 35%, efficiency 30%, SOS 15%, win% 20%
    const composite = netScore * 0.35 + effScore * 0.30 + sosScore * 0.15 + wpScore * 0.20;

    return Math.max(1, Math.min(99, composite));
  };

  const formatPct = (value: number | null | undefined) => {
    if (value == null || Number.isNaN(value)) return '—';
    return `${Math.round(Math.min(100, Math.max(0, value)))}%`;
  };

  const formatRecord = (wins?: number | null, losses?: number | null) => {
    if (wins == null || losses == null) return '—';
    return `${wins}-${losses}`;
  };

  const formatResumeValue = (value: number | null | undefined) => {
    if (value == null || Number.isNaN(value)) return '—';
    return `${value}`;
  };

  const team1RawChance = getTeamBaseWinPct(team1);
  const team2RawChance = getTeamBaseWinPct(team2);
  const chanceTotal = (team1RawChance ?? 0) + (team2RawChance ?? 0);

  const team1WinChance = chanceTotal > 0 && bothTeamsAvailable
    ? ((team1RawChance ?? 0) / chanceTotal) * 100
    : null;
  const team2WinChance = chanceTotal > 0 && bothTeamsAvailable
    ? ((team2RawChance ?? 0) / chanceTotal) * 100
    : null;

  const matchupLabel = bothTeamsAvailable
    ? `${formatPct(team1WinChance)} • ${formatPct(team2WinChance)}`
    : feedingPlayIn
      ? 'vs Play-In Winner'
      : 'Awaiting picks';


  const sizeConfig = {
    sm: { spacing: 'space-y-1', connectorWidth: 'w-8' },
    md: { spacing: 'space-y-2', connectorWidth: 'w-12' },
    lg: { spacing: 'space-y-3', connectorWidth: 'w-16' },
  };

  const config = sizeConfig[size];

  // Card shadow class based on status
  const cardShadowClass = matchupStatus === 'selected'
    ? 'matchup-card-picked'
    : matchupStatus === 'available'
    ? 'matchup-card-available'
    : 'matchup-card';

  // Determine which team slot is the winner (for checkmark display)
  const team1IsWinner = selectedTeamId === game.team1Id && matchupStatus === 'selected';
  const team2IsWinner = selectedTeamId === game.team2Id && matchupStatus === 'selected';
  const showPathFlow = Boolean(currentPick) && isPathSource;

  const waitingForWinner = (team1Available && !team2Available) || (team2Available && !team1Available);

  useEffect(() => {
    const shouldPulse = isNextGameHint && currentPick;
    const hadPick = wasPendingRef.current;

    if (shouldPulse && !hadPick) {
      setShowArrivalPulse(true);
      const timer = window.setTimeout(() => setShowArrivalPulse(false), 1100);
      wasPendingRef.current = true;
      return () => window.clearTimeout(timer);
    }

    wasPendingRef.current = Boolean(currentPick);
    return;
  }, [isNextGameHint, currentPick?.id]);

  return (
    <div
      id={`matchup-${game.id}`}
      className={`relative ${cardShadowClass} rounded-lg ${
        isNextGameHint
          ? `ring-2 ring-primary-400/90 shadow-[0_0_16px_rgba(59,130,246,0.35)] ${
              waitingForWinner ? 'bg-primary-500/20 ring-primary-300/90' : 'bg-primary-500/10'
            }`
          : ''
      } ${className}`}>
      <div className={`${config.spacing} relative`}>
        {/* Card background for active matchups */}
        {bothTeamsAvailable && !isLocked && currentPick && (
          <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 to-accent-500/5 rounded-lg border border-primary-500/20" />
        )}

        {/* Team 1 with optional checkmark */}
        <div className="relative">
          <TeamSlot
            team={team1 || undefined}
            seed={game.team1Seed}
            isSelected={selectedTeamId === game.team1Id}
            isEliminated={selectedTeamId === game.team2Id && !!team1Available}
            isCorrect={(matchupStatus === 'correct' && selectedTeamId === game.team1Id) || false}
            isIncorrect={matchupStatus === 'incorrect' && selectedTeamId === game.team1Id ? true : false}
            isLocked={interactionDisabled}
            disabled={!writeEnabled}
            confidence={selectedTeamId === game.team1Id ? confidence : undefined}
            onClick={() => team1Available && handleTeamSelect(game.team1Id!)}
            size={size}
            showSeed={true}
            showRecord={size !== 'sm'}
            showStatsButton={compact || size === 'sm'}
          />
          {/* Subtle checkmark for winning pick */}
          {team1IsWinner && (
            <div className="absolute -right-1 -top-1 w-4 h-4 bg-primary-500 rounded-full flex items-center justify-center shadow-sm">
              <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
            </div>
          )}
        </div>

        {/* VS indicator — pulse when available and unpicked */}
        <div
          className="relative rounded-lg w-full"
          role="button"
          tabIndex={0}
          aria-label="Open matchup stats"
          aria-expanded={showMatchupStats}
          style={{ touchAction: 'manipulation' }}
          onClick={handleCenterStripMouseClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleMatchupTapTarget();
            }
          }}
          onTouchStart={handleCenterStripTouchStart}
          onTouchEnd={handleCenterStripTouchEnd}
          onTouchCancel={handleCenterStripPointerCancel}
        >
          <div className="relative px-2 py-1 sm:py-2 rounded-lg min-h-[54px] flex flex-col items-center justify-center w-full">
            <div
              className={`relative z-20 pointer-events-none flex flex-col items-center justify-center rounded-lg border transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50 ${
                matchupStatus === 'selected'
                  ? 'bg-primary-500/20 border-primary-500/40 text-primary-400'
                  : matchupStatus === 'correct'
                  ? 'bg-green-500/20 border-green-500/40 text-green-400'
                  : matchupStatus === 'incorrect'
                  ? 'bg-red-500/20 border-red-500/40 text-red-400'
                  : matchupStatus === 'available'
                  ? 'bg-dark-500 border-primary-500/30 text-primary-400/80 vs-pulse'
                  : 'bg-dark-500 border-dark-400 text-text-muted'
              } text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1`}
            >
              {matchupLabel}
            </div>

            {bothTeamsAvailable && (
              <div className="mt-1 flex items-center justify-center gap-1 text-[8px] sm:text-[9px] text-text-secondary">
                <span className="truncate max-w-[48px]">{team1?.shortName || 'Team 1'} {formatPct(team1WinChance)}</span>
                <span>vs</span>
                <span className="truncate max-w-[48px]">{team2?.shortName || 'Team 2'} {formatPct(team2WinChance)}</span>
              </div>
            )}

            {showMatchupStats && bothTeamsAvailable && team1 && team2 && (
              <div
                className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="rounded-lg border border-dark-400 bg-dark-600 text-left shadow-xl">
                  <div className="p-2 flex items-center justify-between border-b border-dark-400">
                    <div className="text-xs font-semibold text-white">Matchup details</div>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setShowMatchupStats(false);
                      }}
                      className="p-1 rounded hover:bg-white/5"
                      aria-label="Close matchup details"
                    >
                      <span className="text-[10px] text-text-secondary">×</span>
                    </button>
                  </div>
                  <div className="p-2 space-y-1.5 text-[11px]">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate max-w-[42%] text-white">{team1.shortName}</span>
                      <span className="text-text-secondary">{formatPct(team1WinChance)} {mmerPrediction ? "MMER win %" : "edge"}</span>
                      <span className="truncate max-w-[42%] text-right text-white">{team2.shortName}</span>
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-text-secondary">
                      <span>Record: {formatRecord(team1.record?.wins, team1.record?.losses)}</span>
                      <ArrowRight className="w-3 h-3" />
                      <span>Record: {formatRecord(team2.record?.wins, team2.record?.losses)}</span>
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-text-secondary">
                      <span>NET: {formatResumeValue(team1.resume?.netRanking)}</span>
                      <ChevronRight className="w-3 h-3" />
                      <span>NET: {formatResumeValue(team2.resume?.netRanking)}</span>
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-text-secondary">
                      <span>SOS: {formatResumeValue(team1.resume?.sos)}</span>
                      <ChevronRight className="w-3 h-3" />
                      <span>SOS: {formatResumeValue(team2.resume?.sos)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {isPlayInGame && !isNextGameHint && (
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full bg-amber-500/30 border border-amber-400/80 text-[11px] text-amber-100 font-semibold whitespace-nowrap">
            Play-In
          </div>
        )}
        {isNextGameHint && (
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full bg-primary-500/30 border border-primary-400/80 text-[11px] text-primary-100 font-semibold whitespace-nowrap shadow-[0_0_10px_rgba(59,130,246,0.35)] animate-pulse">
            {isPlayInGame ? 'Play-In · Next up' : 'Next up'}
          </div>
        )}

        {showArrivalPulse && isNextGameHint && (
          <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 animate-bounce text-primary-200 text-[16px] leading-none">
            <span>↓</span>
          </div>
        )}

        {isPathSource && (
          <div className="absolute -top-5 right-2">
            <button
              type="button"
              onClick={() => onNextMatchup?.()}
              className="sm:hidden text-[10px] rounded-full border border-primary-400/50 bg-primary-500/20 px-2 py-0.5 text-primary-200 hover:bg-primary-500/35"
            >
              Next matchup
            </button>
          </div>
        )}

        {/* Team 2 with optional checkmark */}
        <div className="relative">
          <TeamSlot
            team={team2 || undefined}
            seed={game.team2Seed}
            isSelected={selectedTeamId === game.team2Id}
            isEliminated={selectedTeamId === game.team1Id && !!team2Available}
            isCorrect={(matchupStatus === 'correct' && selectedTeamId === game.team2Id) || false}
            isIncorrect={matchupStatus === 'incorrect' && selectedTeamId === game.team2Id ? true : false}
            isLocked={interactionDisabled}
            disabled={!writeEnabled}
            confidence={selectedTeamId === game.team2Id ? confidence : undefined}
            onClick={() => team2Available && handleTeamSelect(game.team2Id!)}
            size={size}
            showSeed={true}
            showRecord={size !== 'sm'}
            showStatsButton={compact || size === 'sm'}
          />
          {/* Subtle checkmark for winning pick */}
          {team2IsWinner && (
            <div className="absolute -right-1 -top-1 w-4 h-4 bg-primary-500 rounded-full flex items-center justify-center shadow-sm">
              <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
            </div>
          )}
        </div>
      </div>

      {/* Confidence slider — shown for all sizes when pick exists */}
      {showConfidence && currentPick && !compact && (
        <div className={`mt-2 ${size === 'sm' ? 'p-2' : 'p-3'} bg-dark-500 rounded-lg border border-dark-400`}>
          <ConfidenceSlider
            value={confidence}
            onChange={handleConfidenceChange}
            disabled={interactionDisabled}
            size="sm"
            showPreview={size !== 'sm'}
          />
        </div>
      )}

      {/* Game status indicators */}
      <div className="absolute -top-2 -right-2 flex space-x-1">
        {isLocked && (
          <div className="w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
            <div className="w-2 h-2 bg-yellow-700 rounded-full" />
          </div>
        )}

        {currentPick && confidence >= 8 && (
          <div className="w-4 h-4 bg-primary-500 rounded-full flex items-center justify-center">
            <div className="w-2 h-2 bg-white rounded-full" />
          </div>
        )}

        {currentPick && selectedTeamId && (
          (() => {
            const selectedTeam = storeGetTeam(selectedTeamId) ?? getStaticTeamById(selectedTeamId);
            const selectedSeed = selectedTeam?.seed || 0;
            if (selectedSeed >= 10) {
              return (
                <div className="w-4 h-4 bg-orange-500 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full" />
                </div>
              );
            }
            return null;
          })()
        )}
      </div>

      {/* Connection line to next round */}
      {game.nextGameId && (
        <div className="absolute left-full top-1/2 -translate-y-1/2 flex items-center gap-0">
          <div className={`${config.connectorWidth} h-1 bg-dark-300`} />
          {showPathFlow && !compact && (
            <div
              className={`h-1 ${config.connectorWidth} bg-primary-500 transition-all duration-500 ease-out origin-left scale-x-100 opacity-100 animate-pulse shadow-[0_0_10px_rgba(59,130,246,0.8)]`}
              style={{ transform: 'scaleX(1)' }}
            />
          )}
        </div>
      )}

      {/* Pending overlay for TBD teams */}
      {!bothTeamsAvailable && (
        <div
          className={`absolute inset-0 rounded-lg flex items-center justify-center backdrop-blur-sm ${
            isNextGameHint
              ? 'bg-primary-600/25 ring-2 ring-primary-400/80 animate-pulse'
              : 'bg-dark-700/60'
          }`}
        >
          <div className="text-center px-2">
            <div className={`text-[11px] font-medium ${isNextGameHint ? 'text-primary-200' : 'text-text-muted'}`}>
              {rawGame.round === 'first_round' && !bothTeamsAvailable
                ? '🏀 First Four Winner'
                : waitingForWinner
                  ? 'Winner appears here'
                  : 'Awaiting picks'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Matchup = React.memo(MatchupComponent);

export default Matchup;
